import { useState, useRef, useLayoutEffect, useContext, useMemo } from "react";
import { Grid, IconButton, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { categoriesTransition, parentMotion } from "../animation-variants";
import Category from "./Category";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { translation } from "./Dictionary";
import { AppContext } from "../contextProviders/AppContext";
import { typeOfProducts } from "../shared/ProductClassification/type-of-products";
import { Storage } from "aws-amplify";

export default function Categories({
  offers,
  selectedCategory,
  selectedCompany,
  setSelectedCategory,
}) {
  const { language, typeOfBusiness, FEATURE_USE_PRODUCT_TYPE_CATEGORIES } =
    useContext(AppContext);
  const theme = useTheme();
  const categoriesContainerRef = useRef();
  const categoriesRef = useRef();

  const defaultCategories = useMemo(() => {
    // Organization offers are in bundle, so each offer is one category
    if (typeOfBusiness === "organization") {
      if (FEATURE_USE_PRODUCT_TYPE_CATEGORIES) {
        if (offers === "no offers") {
          return null;
        } else {
          const vehicleClasses = typeOfProducts.map((vehicleClass) => {
            const categoryIds = [];
            offers.forEach((offer) => {
              if (vehicleClass.id === offer.vehicle?.category?.typeId) {
                categoryIds.push(offer.vehicle.category.id);
              }
            });
            return {
              ...vehicleClass,
              id: categoryIds,
              typeId: vehicleClass.id,
            };
          });

          return vehicleClasses.filter(
            (vehicleClass) => vehicleClass?.id.length > 0
          );
        }
      } else {
        return offers.map((offer) => {
          return { ...offer.vehicle.category };
        });
      }
    }

    if (typeOfBusiness === "legacy") {
      // legacy users get categories from companies
      return selectedCompany.categories?.items;
    }
  }, [
    typeOfBusiness,
    selectedCompany,
    offers,
    FEATURE_USE_PRODUCT_TYPE_CATEGORIES,
  ]);
  const categories = useMemo(() => {
    // initial categories

    if (typeOfBusiness === "legacy") {
      // sort products into categories

      const listOfCategories = [];

      defaultCategories.forEach((cat) => {
        if (!offers || offers === "no offers") {
          return;
        }

        offers.forEach((offer) => {
          const isInCategory = cat.id === offer.vehicle.category?.id;
          if (
            isInCategory &&
            !listOfCategories.some((c) => c.id === offer.vehicle.category?.id)
          ) {
            listOfCategories.push(cat);
          }
        });
      });

      return listOfCategories;
    }

    return defaultCategories;
  }, [offers, defaultCategories, typeOfBusiness]);

  const scroll = (scrollOffset) => {
    categoriesRef.current.scrollLeft += scrollOffset;
  };

  const handleCategoryChange = (id) => {
    if (id === selectedCategory) return;
    if (typeOfBusiness === "legacy") {
      if (id === "selectAllCategories") {
        setSelectedCategory("selectAllCategories");
      } else {
        setSelectedCategory(id);
      }
    }
    if (typeOfBusiness === "organization") {
      if (id === "selectAllCategories") {
        setSelectedCategory("selectAllCategories");
      } else {
        setSelectedCategory(id);
      }
    }
  };

  // Determine if navigation buttons should be shown
  const [categoryNav, setCategoryNav] = useState(false);

  useLayoutEffect(() => {
    function resizeHandler() {
      if (!categoriesRef.current) return;
      if (
        categoriesRef.current.clientWidth < categoriesRef.current.scrollWidth
      ) {
        setCategoryNav(true);
      } else {
        setCategoryNav(false);
      }
    }
    window.addEventListener("resize", resizeHandler);
    resizeHandler();
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  if (offers === "no offers") return null;

  if (categories.length !== 0)
    return (
      <Grid
        container
        item
        alignItems="center"
        xs={12}
        sx={{
          // maxWidth: "inherit",
          position: "relative",
          flexWrap: "nowrap",
        }}
      >
        {categoryNav && (
          <Grid style={{}}>
            <IconButton
              onClick={() => scroll(-150)}
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.75em",
                  [theme.breakpoints.down("md")]: {
                    fontSize: "1.25em",
                  },
                },
              }}
              size="large"
            >
              <ChevronLeftIcon style={{ color: theme.palette.primary.icon }} />
            </IconButton>
          </Grid>
        )}

        <Grid
          container
          justifyContent="center"
          ref={categoriesContainerRef}
          style={{
            // Issues with hiding chrome scrollbars, this line hides it, but disables scrolling aswell
            // overflowX: "hidden",
            // maxWidth: "82%",
            scrollBehavior: "smooth",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            maxWidth: "inherit",
            overflow: "hidden",
            minHeight: "125px",
          }}
        >
          <motion.div
            ref={categoriesRef}
            initial="initial"
            animate="enter"
            exit="exit"
            variants={parentMotion}
            /*     className={classes.catContainer} */
            style={{
              justifyContent: categoryNav ? "flex-start" : "center",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              scrollBehavior: "smooth",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              overflow: "hidden",
            }}
          >
            <motion.div
              key="kofdf-tg43vbwsd-4t35ts-fse45r-tfgs44gfv-rf34"
              whileHover={{ scale: 1.1 }}
              variants={categoriesTransition}
              style={{
                margin: 4,
                minHeight: 90,
                minWidth: 90,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: 3,
                marginTop: 8,
              }}
            >
              <div
                onClick={() => handleCategoryChange("selectAllCategories")}
                style={{
                  minHeight: 60,
                  width: 70,
                  marginBottom: 10,
                  borderRadius: 20,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  scale:
                    selectedCategory === "selectAllCategories" ? "1.3" : "1",
                  opacity: selectedCategory === "selectAllCategories" ? 1 : 0.7,
                  /*   borderStyle:
                    selectedCategory === "selectAllCategories" ? "solid" : null,
                  borderWidth:
                    selectedCategory === "selectAllCategories" ? 1 : null,
                  borderColor:
                    selectedCategory === "selectAllCategories"
                      ? theme.palette.primary.main
                      : null, */
                }}
              >
                <DoneAllIcon
                  style={{
                    fontSize: 40,
                    color: theme.palette.secondary.main,
                  }}
                />
              </div>

              <Typography
                align="left"
                variant="body1"
                style={{
                  fontWeight:
                    selectedCategory === "selectAllCategories" ? 600 : 400,
                  color: theme.palette.primary.mainText,
                }}
              >
                {translation.get(language, "All categories text")}
              </Typography>
            </motion.div>

            {categories.map((item, index) => {
              return (
                <Category
                  key={typeof item.id === "string" ? item?.id : item.name}
                  item={item}
                  selectedCategory={selectedCategory}
                  theme={theme}
                  handleCategoryChange={handleCategoryChange}
                />
              );
            })}
          </motion.div>
        </Grid>
        {categoryNav && (
          <Grid style={{}}>
            <IconButton
              onClick={() => scroll(150)}
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.75em",
                  [theme.breakpoints.down("md")]: {
                    fontSize: "1.25em",
                  },
                },
              }}
              size="large"
            >
              <ChevronRightIcon style={{ color: theme.palette.primary.icon }} />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
}
